import { nanoid } from "nanoid"
import Logger from "@/utils/Logger"
import { typesenseApiKeyCacheKey } from "@/utils/typesenseApiKeyCacheKey"

export async function useTypesenseApiKey(targetJobShops?: string | Array<{ label: string, value: string, checked: boolean }>) {
  const { $api } = useNuxtApp()
  const { tenantId, jobShopId, jobShopVanity: headerVanity } = useHeaderStore()
  const hasExplicitVanity = Array.isArray(targetJobShops)
    ? targetJobShops.length > 0
    : !!targetJobShops

  const vanity = hasExplicitVanity && targetJobShops
    ? targetJobShops
    : headerVanity

  const requestId = nanoid()
  const vanityString = Array.isArray(vanity) ? vanity.map(v => v.value).join(", ") : vanity
  const contextString = `TypeSense API key for tenantId: "${tenantId}", jobShopId: "${jobShopId}" and vanity: "${vanityString}"`

  let startTime
  if (import.meta.server) {
    Logger(requestId).start(`Start fetching ${contextString}`)
    startTime = Date.now()
  }

  const cacheKey = typesenseApiKeyCacheKey(jobShopId, headerVanity, vanity)
  // Refreshes this same cache entry, so a 401 re-fetches the key for this filter.
  const { refreshTypesenseApiKey } = useTypesenseKeyRefresh(cacheKey)

  const {
    data: typesenseApiKey,
    pending: typesenseApiKeyPending,
    error: typesenseApiKeyError,
  } = await useAsyncData(cacheKey, async () => {
    const response = await $api.searchAndFilters.getTypesenseApiKey(tenantId, vanity)
    return response.key
  })

  if (import.meta.server) {
    if (!typesenseApiKeyError.value) {
      const duration = Date.now() - startTime
      Logger(requestId).success(`Successfully fetched ${contextString}. (${duration}ms)`)
    }
    else {
      const errorMessage = typesenseApiKeyError.value?.data?.errors?.[0]?.message || typesenseApiKeyError.value?.data?.description || "Unknown error occurred"
      Logger(requestId).error(`Error while fetching ${contextString}: ${errorMessage}\n\n`, typesenseApiKeyError.value)
    }
  }

  return { typesenseApiKey, typesenseApiKeyPending, typesenseApiKeyError, refreshTypesenseApiKey }
}
